// ============================================================================
// DRAWING MANAGER V3 - Type Definitions
// Enhanced drawing management with folders, markups, and version control
// Date: January 2026
// ============================================================================

export interface DrawingFolder {
  id: string;
  client_id: number;
  parent_folder_id?: string | null;
  parent_id?: string | null;
  name: string;
  path: string;
  drawing_count: number;
  created_at?: string;
  updated_at?: string;
  children?: DrawingFolder[];
}

export interface Drawing {
  id: string;
  client_id: number;
  folder_id: string | null;
  work_item_id: string | null;
  drawing_number: string | null;
  drawing_name: string;
  description: string | null;
  revision: string;
  version: number;
  file_url: string;
  file_type: string;
  file_size: number;
  original_filename: string;
  thumbnail_url: string | null;
  status: DrawingStatus;
  is_published: boolean;
  published_at: string | null;
  published_by: number | null;
  // Enhanced metadata
  drawing_type: DrawingType | null;
  discipline: DrawingDiscipline | null;
  sheet_size: SheetSize | null;
  scale: string | null;
  material_specification: string | null;
  finish_requirements: string | null;
  tolerance_class: string | null;
  is_critical: boolean;
  tags: string[];
  metadata: Record<string, any>;
  // Audit
  created_at: string;
  created_by: number;
  updated_at: string;
  // Relationships
  markup_count?: number;
  comment_count?: number;
  version_history?: DrawingVersion[];
}

export type DrawingStatus = 
  | 'draft' 
  | 'in_review' 
  | 'pending_approval'
  | 'pending_review'
  | 'approved' 
  | 'published' 
  | 'superseded' 
  | 'obsolete'
  | 'archived';

export type DrawingType = 
  | 'floor_plan'
  | 'section'
  | 'elevation'
  | 'detail'
  | 'assembly'
  | 'pid'  // P&ID
  | 'schematic'
  | 'wiring_diagram'
  | 'layout'
  | 'isometric'
  | 'general_arrangement'
  | 'shop_drawing'
  | 'as_built'
  | 'other';

export type DrawingDiscipline = 
  | 'architectural'
  | 'structural'
  | 'mechanical'
  | 'electrical'
  | 'plumbing'
  | 'civil'
  | 'hvac'
  | 'fire_protection'
  | 'landscape'
  | 'interior'
  | 'process'
  | 'instrumentation'
  | 'other';

export type SheetSize = 
  | 'A0' | 'A1' | 'A2' | 'A3' | 'A4'
  | 'ARCH_E' | 'ARCH_D' | 'ARCH_C' | 'ARCH_B' | 'ARCH_A'
  | 'ANSI_E' | 'ANSI_D' | 'ANSI_C' | 'ANSI_B' | 'ANSI_A'
  | 'custom';

export interface DrawingVersion {
  id: string;
  drawing_id: string;
  version: number;
  revision: string;
  file_url: string;
  file_type?: string | null;
  file_size: number;
  changes_summary: string | null;
  change_description?: string | null;
  created_at: string;
  created_by: number;
  created_by_name?: string;
  uploaded_by_name?: string | null;
}

export interface DrawingMarkupLayer {
  name: string;
  color: string;
  is_visible: boolean;
  is_locked: boolean;
  order?: number;
}

export interface DrawingMarkup {
  id: string;
  drawing_id: string;
  user_id: number;
  user_name?: string;
  markup_type: MarkupType;
  markup_data: MarkupData;
  layer_name: string;
  color: string;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export type MarkupType = 
  | 'cloud'
  | 'arrow'
  | 'text'
  | 'rectangle'
  | 'circle'
  | 'line'
  | 'polyline'
  | 'freehand'
  | 'dimension'
  | 'callout'
  | 'highlight'
  | 'stamp'
  | 'image'
  | 'polygon'
  | 'ellipse';

export interface MarkupData {
  points?: { x: number; y: number }[];
  text?: string;
  fontSize?: number;
  strokeWidth?: number;
  fillColor?: string;
  strokeColor?: string;
  arrowHead?: boolean;
  radius?: number;
  width?: number;
  height?: number;
}

export interface DrawingComment {
  id: string;
  drawing_id: string;
  user_id: number;
  user_name?: string;
  user_avatar?: string;
  parent_comment_id: string | null;
  parent_id?: string | null;
  content: string;
  pin_x: number | null;
  pin_y: number | null;
  position_x?: number | null;
  position_y?: number | null;
  is_resolved: boolean;
  resolved_by: number | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  replies?: DrawingComment[];
}

export interface TransmittalItem {
  drawing_id?: string;
  drawing_number: string;
  title: string;
  revision: string;
  copies: number;
  format?: string;
  remarks?: string | null;
}

export interface DrawingTransmittal {
  id: string;
  client_id?: number;
  transmittal_number: string;
  title?: string;
  recipient_name?: string;
  recipient_email?: string;
  recipient_company?: string;
  purpose?: TransmittalPurpose;
  project_name?: string;
  project_number?: string;
  from_company?: string;
  from_name?: string;
  from_email?: string;
  from_phone?: string;
  to_company?: string;
  to_name?: string;
  to_email?: string;
  to_address?: string;
  subject?: string;
  reason?: TransmittalPurpose;
  notes: string | null;
  drawings?: Drawing[];
  items?: TransmittalItem[];
  copies_to?: string[];
  status?: string;
  date?: string;
  sent_at: string | null;
  created_at: string;
  created_by?: number;
  updated_at?: string;
}

export type TransmittalPurpose = 
  | 'for_approval'
  | 'for_review'
  | 'for_information'
  | 'for_construction'
  | 'as_requested'
  | 'for_record'
  | 'resubmission';

// View and filter types
export type ViewMode = 'grid' | 'list' | 'tree';

export interface DrawingFilters {
  search: string;
  status: DrawingStatus | 'all';
  discipline: DrawingDiscipline | 'all';
  type: DrawingType | 'all';
  fileType: string | 'all';
  folderId: string | null;
  dateRange: { start: string | null; end: string | null };
  tags: string[];
}

// Config constants
export const DRAWING_DISCIPLINES: { value: DrawingDiscipline; label: string; icon: string }[] = [
  { value: 'architectural', label: 'Architectural', icon: '🏛️' },
  { value: 'structural', label: 'Structural', icon: '🏗️' },
  { value: 'mechanical', label: 'Mechanical', icon: '⚙️' },
  { value: 'electrical', label: 'Electrical', icon: '⚡' },
  { value: 'plumbing', label: 'Plumbing', icon: '🚿' },
  { value: 'civil', label: 'Civil', icon: '🛤️' },
  { value: 'hvac', label: 'HVAC', icon: '❄️' },
  { value: 'fire_protection', label: 'Fire Protection', icon: '🔥' },
  { value: 'landscape', label: 'Landscape', icon: '🌳' },
  { value: 'interior', label: 'Interior', icon: '🛋️' },
  { value: 'process', label: 'Process', icon: '🔄' },
  { value: 'instrumentation', label: 'Instrumentation', icon: '📊' },
  { value: 'other', label: 'Other', icon: '📄' },
];

export const DRAWING_TYPES: { value: DrawingType; label: string }[] = [
  { value: 'floor_plan', label: 'Floor Plan' },
  { value: 'section', label: 'Section' },
  { value: 'elevation', label: 'Elevation' },
  { value: 'detail', label: 'Detail' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'pid', label: 'P&ID' },
  { value: 'schematic', label: 'Schematic' },
  { value: 'wiring_diagram', label: 'Wiring Diagram' },
  { value: 'layout', label: 'Layout' },
  { value: 'isometric', label: 'Isometric' },
  { value: 'general_arrangement', label: 'General Arrangement' },
  { value: 'shop_drawing', label: 'Shop Drawing' },
  { value: 'as_built', label: 'As-Built' },
  { value: 'other', label: 'Other' },
];

export const SHEET_SIZES: { value: SheetSize; label: string; dimensions: string }[] = [
  { value: 'A0', label: 'A0', dimensions: '841 × 1189 mm' },
  { value: 'A1', label: 'A1', dimensions: '594 × 841 mm' },
  { value: 'A2', label: 'A2', dimensions: '420 × 594 mm' },
  { value: 'A3', label: 'A3', dimensions: '297 × 420 mm' },
  { value: 'A4', label: 'A4', dimensions: '210 × 297 mm' },
  { value: 'ARCH_E', label: 'ARCH E', dimensions: '36 × 48 in' },
  { value: 'ARCH_D', label: 'ARCH D', dimensions: '24 × 36 in' },
  { value: 'ARCH_C', label: 'ARCH C', dimensions: '18 × 24 in' },
  { value: 'ARCH_B', label: 'ARCH B', dimensions: '12 × 18 in' },
  { value: 'ARCH_A', label: 'ARCH A', dimensions: '9 × 12 in' },
  { value: 'custom', label: 'Custom', dimensions: '' },
];

export const STATUS_CONFIG: Record<DrawingStatus, { label: string; color: string; bgColor: string; icon: string }> = {
  draft: { label: 'Draft', color: 'text-gray-700', bgColor: 'bg-gray-100', icon: '📝' },
  in_review: { label: 'In Review', color: 'text-blue-700', bgColor: 'bg-blue-100', icon: '🔍' },
  pending_approval: { label: 'Pending Approval', color: 'text-yellow-700', bgColor: 'bg-yellow-100', icon: '⏳' },
  pending_review: { label: 'Pending Review', color: 'text-amber-700', bgColor: 'bg-amber-100', icon: '🕒' },
  approved: { label: 'Approved', color: 'text-green-700', bgColor: 'bg-green-100', icon: '✓' },
  published: { label: 'Published', color: 'text-green-800', bgColor: 'bg-green-200', icon: '✓' },
  superseded: { label: 'Superseded', color: 'text-orange-700', bgColor: 'bg-orange-100', icon: '⚠️' },
  obsolete: { label: 'Obsolete', color: 'text-red-700', bgColor: 'bg-red-100', icon: '✕' },
  archived: { label: 'Archived', color: 'text-slate-700', bgColor: 'bg-slate-100', icon: '🗄️' },
};

export const FILE_TYPE_CONFIG: Record<string, { icon: string; label: string; canPreview: boolean }> = {
  pdf: { icon: '📄', label: 'PDF', canPreview: true },
  dwg: { icon: '📐', label: 'AutoCAD', canPreview: false },
  dxf: { icon: '📐', label: 'DXF', canPreview: false },
  stp: { icon: '🔧', label: 'STEP', canPreview: false },
  step: { icon: '🔧', label: 'STEP', canPreview: false },
  igs: { icon: '🔧', label: 'IGES', canPreview: false },
  iges: { icon: '🔧', label: 'IGES', canPreview: false },
  png: { icon: '🖼️', label: 'PNG', canPreview: true },
  jpg: { icon: '🖼️', label: 'JPEG', canPreview: true },
  jpeg: { icon: '🖼️', label: 'JPEG', canPreview: true },
  tiff: { icon: '🖼️', label: 'TIFF', canPreview: true },
  tif: { icon: '🖼️', label: 'TIFF', canPreview: true },
  bmp: { icon: '🖼️', label: 'BMP', canPreview: true },
  svg: { icon: '🎨', label: 'SVG', canPreview: true },
  webp: { icon: '🖼️', label: 'WebP', canPreview: true },
};
